/**
 * <s-hero-slideshow> — Auto-advancing, cross-fading hero slideshow.
 *
 * Slides stack on top of each other; the active one carries [data-active]
 * and CSS fades it in. Inactive slides are inert so their links are skipped.
 * Autoplay pauses on hover/focus, can be paused with the toggle, and never
 * starts for shoppers who prefer reduced motion.
 *
 * Required child markup:
 *   [data-slide]              — Each slide (first one starts with data-active).
 *   [data-slide-to]           — Optional buttons with data-index (0-based).
 *   [data-slideshow-toggle]   — Optional pause/play button (aria-pressed = paused).
 *
 * Element dataset:
 *   data-interval — milliseconds between slides (default 6500).
 */
class SHeroSlideshow extends HTMLElement {
  #ac;
  #timer;
  #slides = [];
  #index = 0;
  #paused = false;
  #hovering = false;

  connectedCallback() {
    this.#slides = Array.from(this.querySelectorAll('[data-slide]'));
    if (this.#slides.length < 2) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    for (const dot of this.querySelectorAll('[data-slide-to]')) {
      dot.addEventListener('click', () => {
        this.show(Number(dot.dataset.index));
        this.#restart();
      }, opts);
    }

    const toggle = this.querySelector('[data-slideshow-toggle]');
    toggle?.addEventListener('click', () => {
      this.#paused = !this.#paused;
      toggle.setAttribute('aria-pressed', String(this.#paused));
      this.#restart();
    }, opts);

    this.addEventListener('pointerenter', () => { this.#hovering = true; this.#stop(); }, opts);
    this.addEventListener('pointerleave', () => { this.#hovering = false; this.#restart(); }, opts);
    this.addEventListener('focusin', () => this.#stop(), opts);
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.#restart();
    }, opts);

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.#paused = true;
      toggle?.setAttribute('aria-pressed', 'true');
    }

    this.show(0);
    this.#restart();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#stop();
  }

  show(index) {
    const count = this.#slides.length;
    this.#index = ((index % count) + count) % count;
    this.#slides.forEach((slide, i) => {
      const active = i === this.#index;
      slide.toggleAttribute('data-active', active);
      slide.inert = !active;
    });
    // Optional captions (e.g. the changing city name) follow the active slide by position.
    this.querySelectorAll('[data-city]').forEach((city, i) => {
      city.toggleAttribute('data-active', i === this.#index);
    });
    for (const dot of this.querySelectorAll('[data-slide-to]')) {
      if (Number(dot.dataset.index) === this.#index) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    }
  }

  #stop() {
    clearInterval(this.#timer);
    this.#timer = undefined;
  }

  #restart() {
    this.#stop();
    if (this.#paused || this.#hovering) return;
    const interval = Number(this.dataset.interval) || 6500;
    this.#timer = setInterval(() => this.show(this.#index + 1), interval);
  }
}

if (!customElements.get('s-hero-slideshow')) customElements.define('s-hero-slideshow', SHeroSlideshow);
